<template>
  <router-link
    :to="{
      name: 'EventPage',
      params: { type: event.type.path, id: event.slug },
    }"
    class="no-underline hover:no-underline"
  >
    <div
      :class="[
        'flex flex-col h-full rounded-lg bg-white shadow-xl transform hover:-translate-y-1 hover:shadow-xl transition duration-300 ease-in-out overflow-hidden',
        event.featured ? 'border-4 border-yellow-200' : '',
        event.featured ? 'shadow-lg shadow-yellow-500/50' : '',
      ]"
    >
      <!-- Media content -->
      <div
        class="relative cursor-pointer"
        @mouseenter="playVideo"
        @mouseleave="stopVideo"
      >
        <div
          class="relative overflow-hidden text-white shadow-lg bg-gray-800 rounded-lg"
        >
          <template v-if="event.videos?.promo?.video?.url">
            <img
              v-show="!hovering"
              class="w-full aspect-video object-cover"
              :src="
                event.images.thumbnail.large?.url ||
                'https://static.ne.twork.cz/images/no_event_image.webp'
              "
              :alt="event.title"
            />
            <video
              v-show="hovering"
              ref="video"
              class="w-full aspect-video object-cover pointer-events-none"
              muted
              loop
              playsinline
              preload="metadata"
            >
              <source :src="event.videos.promo.video.url" type="video/mp4" />
            </video>
          </template>
          <template
            v-else-if="event.images.thumbnail && event.images.thumbnail.large"
          >
            <img
              class="w-full aspect-video object-cover"
              :src="
                event.images.thumbnail.large.url ||
                'https://static.ne.twork.cz/images/no_event_image.webp'
              "
              :alt="event.title"
            />
          </template>
          <template v-else>
            <img
              class="w-full aspect-video object-cover"
              src="https://static.ne.twork.cz/images/no_event_image.webp"
              alt="No Image Available"
            />
          </template>

          <!-- Bottom-right restricted badge -->
          <div
            v-if="isRestricted"
            class="absolute bottom-2 right-2 bg-red-600 text-white text-xs font-bold px-2 py-1 rounded"
          >
            {{ $translate("Restricted") }}
          </div>
        </div>

        <!-- Status with Icon -->
        <div
          class="absolute bottom-0 left-1/2 transform -translate-x-1/2 text-center text-xs whitespace-nowrap shadow-xl"
        >
          <!-- Status Text with Icon -->
          <div
            v-if="event.status !== 'upcoming' && statusInfo.text"
            :class="[
              statusInfo.bgcolor,
              'py-1 px-3 inline-flex items-center shadow-lg rounded-tl-lg rounded-tr-lg',
            ]"
          >
            <font-awesome-icon
              :icon="statusInfo.icon"
              :class="
                statusInfo.txtcolor + ' mr-2 ' + statusInfo.animationClass
              "
              aria-hidden="true"
            />
            <span :class="statusInfo.txtcolor + ' font-mono text-sm'">{{
              statusInfo.text
            }}</span>
          </div>
          <!-- Countdown Text -->
          <!-- Countdown with Hourglass Icon -->
          <div
            v-else-if="event.status === 'upcoming' && countdown"
            :class="[
              'py-1 px-3 rounded-tl-lg rounded-tr-lg shadow-lg flex items-center space-x-1',
              getCountdownColor().bgcolor,
            ]"
          >
            <font-awesome-icon
              icon="hourglass-half"
              :class="getCountdownColor().txtcolor + ' mr-1'"
              aria-hidden="true"
            />
            <span
              class="countdown-text font-mono text-sm truncate"
              :class="getCountdownColor().txtcolor"
              title="Countdown"
            >
              {{ countdown }}
            </span>
          </div>
        </div>
      </div>

      <!-- Price and badges -->
      <!-- PRICE & DISCOUNT BADGES (top-left corner, stacked) -->
      <div class="absolute top-0 left-0 flex flex-col space-y-1">
        <!-- Price badge -->
        <div
          :class="[
            'text-white text-xs px-2 py-1 rounded-br-lg',
            event.price ? 'bg-orange-600' : 'bg-green-600',
          ]"
        >
          <span v-if="!event.price">{{ $translate("Free") }}</span>
          <span v-else>{{ event.price.formatted }}</span>
        </div>

        <!-- Discount badge (only shown if there's a discount) -->
        <div
          v-if="event.price && event.price.discount"
          class="flex items-center bg-red-600 text-white text-xs font-bold px-2 py-1 rounded-br-lg"
        >
          <font-awesome-icon icon="bomb" class="mr-1" />
          -{{ event.price.discount.percentage }}%
        </div>
      </div>

      <div v-if="event.featured" class="absolute top-2 right-2 text-yellow-300">
        <font-awesome-icon icon="bolt" class="text-2xl flash-animation" />
      </div>

      <!-- Content section -->
      <div class="p-4 flex-grow">
        <div class="flex justify-between items-center gap-2">
          <div
            class="text-xs font-semibold uppercase tracking-wide text-gray-400"
          >
            {{ event.type.name }}
          </div>
          <div
            v-if="event.date && event.date.start_date"
            class="text-xs text-gray-600 flex items-center"
          >
            <span class="formatted-date">{{ formattedDate }}</span>
          </div>
        </div>

        <div class="flex items-center mt-1">
          <font-awesome-icon
            v-if="isRestricted"
            :icon="['fas', 'lock']"
            class="text-red-500 mr-2"
          />
          <h5 class="text-lg font-bold leading-tight">{{ event.title }}</h5>
        </div>
        <p v-if="event.excerpt" class="text-sm text-gray-600 mt-2">
          {{ event.excerpt }}
        </p>
      </div>

      <!-- Footer section - always at the bottom -->
      <footer class="p-3 bg-gray-100 gap-1 flex justify-between items-center">
        <!-- Show stars and rating count if ratings are available -->
        <div
          v-if="event.ratings.count > 0"
          class="flex items-center space-x-2 xl:mr-1"
        >
          <div class="flex items-center">
            <font-awesome-icon
              v-for="n in 5"
              :key="n"
              :icon="getStarIcon(event.ratings.overall / 2, n)"
              class="stars-icon text-yellow-400"
            />
          </div>
          <span class="text-xs text-gray-600"
            >({{ event.ratings.overall }}/10)</span
          >
        </div>

        <!-- Always align the button to the right with ml-auto -->
        <span
          class="inline-block font-bold py-2 px-4 rounded-lg transition duration-150 ease-in-out ml-auto lg:whitespace-nowrap xl:text-sm"
          :class="[
            getColor(600, 'bg'),
            getColor(50, 'text'),
            getColor(700, 'hover:bg'),
          ]"
        >
          <span
            class="flex items-center text-white"
            :class="{
              'text-sm': event.ratings.count > 0,
              'text-base': event.ratings.count === 0,
            }"
          >
            {{ btnTypeText }}
            <font-awesome-icon icon="arrow-right" class="ml-2" />
          </span>
        </span>
      </footer>
    </div>
  </router-link>
</template>
<script>
import {
  durationFormatted,
  getStatusInfo,
  btnTypeText,
  getColorClass,
  getEventType,
} from "@/utils/helper.js";
import { translate } from "@/utils/translate"; // Import translate function
import { FontAwesomeIcon } from "@fortawesome/vue-fontawesome";
import { library } from "@fortawesome/fontawesome-svg-core";
import { faStar, faStarHalfAlt } from "@fortawesome/free-solid-svg-icons";
import { faStar as faStarEmpty } from "@fortawesome/free-regular-svg-icons"; // Empty star from regular set

// Add icons to the library
library.add(faStar, faStarHalfAlt, faStarEmpty);
export default {
  name: "EventCard",
  components: {
    FontAwesomeIcon,
  },
  props: {
    event: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      countdown: null,
      hovering: false, // State to track whether the video should play or not
    };
  },
  computed: {
    isRestricted() {
      // Check if the event has restrictions (password or tag)
      return (
        this.event.restriction &&
        (this.event.restriction === "password" ||
          this.event.restriction === "tags")
      );
    },
    btnTypeText() {
      return btnTypeText(this.event.type.type, this.event.status);
    },
    formattedTimeLimit() {
      return durationFormatted(this.event.time_limit.raw);
    },
    statusInfo() {
      return getStatusInfo(this.event);
    },

    statusIcon() {
      switch (this.event.status) {
        case "planned":
          return ["fas", "calendar-alt"];
        case "upcoming":
          return ["fas", "hourglass-half"];
        case "live":
          return ["fas", "circle"];
        case "ended":
          return ["fas", "stop-circle"];
        case "playback":
          return ["fas", "play-circle"];
        default:
          return ["fas", "question-circle"];
      }
    },
    formattedDate() {
      // Parse the date strings properly
      const startDate = new Date(this.event.date.raw.start_at);
      const endDate = new Date(this.event.date.raw.end_at);

      if (isNaN(startDate) || isNaN(endDate)) {
        console.error(
          "Invalid date format:",
          this.event.date.raw.start_at,
          this.event.date.raw.end_at
        );
        return "Invalid Date";
      }

      const dateOptions = { day: "numeric", month: "long" }; // Format with full month name
      const startDateStr = `${startDate.toLocaleDateString(
        "cs-CZ",
        dateOptions
      )} ${translate("from")} ${this.formatTime(this.event.date.raw.start_at)}`;
      const endDateStr = `${endDate.toLocaleDateString(
        "cs-CZ",
        dateOptions
      )} ${this.formatTime(this.event.date.raw.end_at)}`;

      if (this.isSingleDayEvent) {
        return `${startDateStr} (${this.event.date.duration.text})`;
      } else {
        return `${startDateStr} ${endDateStr} (${this.event.date.duration.text})`;
      }
    },
    isSingleDayEvent() {
      return (
        new Date(this.event.date.raw.start_at).toDateString() ===
        new Date(this.event.date.raw.end_at).toDateString()
      );
    },
  },
  methods: {
    formatTime(dateString) {
      const date = new Date(dateString);
      if (isNaN(date)) {
        console.error("Invalid time format:", dateString);
        return "Invalid Time";
      }
      const options = { hour: "2-digit", minute: "2-digit" };
      return date.toLocaleTimeString("cs-CZ", options);
    },
    getColor(shade = 500, prefix = "text") {
      return getColorClass(getEventType(this.event.type.type), shade, prefix);
    },
    getStarIcon(rating, starIndex) {
      if (starIndex <= rating) {
        return "fas fa-star"; // Full star
      } else if (starIndex - 0.5 <= rating) {
        return "fas fa-star-half-alt"; // Half star
      } else {
        return "far fa-star"; // Empty star
      }
    },

    getCountdownColor() {
      const now = new Date().getTime();
      const start = new Date(this.event.date.raw.start_at).getTime();
      const timeDifference = start - now;

      // Define the time thresholds
      const oneDay = 24 * 60 * 60 * 1000; // 1 day in milliseconds
      const twoDays = 2 * oneDay;
      const threeDays = 3 * oneDay;
      const oneWeek = 7 * oneDay;
      const twoWeeks = 14 * oneDay;

      // Determine color based on time remaining
      if (timeDifference <= oneDay) {
        return { bgcolor: "bg-red-600", txtcolor: "text-white" };
      } else if (timeDifference <= twoDays) {
        return { bgcolor: "bg-red-400", txtcolor: "text-gray-800" };
      } else if (timeDifference <= threeDays) {
        return { bgcolor: "bg-red-300", txtcolor: "text-gray-800" };
      } else if (timeDifference <= oneWeek) {
        return { bgcolor: "bg-yellow-500", txtcolor: "text-gray-800" };
      } else if (timeDifference <= twoWeeks) {
        return { bgcolor: "bg-yellow-300", txtcolor: "text-gray-800" };
      } else {
        return { bgcolor: "bg-yellow-100", txtcolor: "text-gray-800" };
      }
    },

    /**
     * Updates the countdown timer.
     * Formats the countdown with abbreviated units and updates the countdown text.
     */
    updateCountdown() {
      const now = new Date().getTime();
      const start = new Date(this.event.date.raw.start_at).getTime();
      const end = new Date(this.event.date.raw.end_at).getTime();

      if (now > end) {
        clearInterval(this.countdownInterval);
        this.countdown = translate("countdown.event_ended");
        return;
      }

      if (now >= start && now <= end) {
        clearInterval(this.countdownInterval);
        this.countdown = translate("countdown.event_started");
        return;
      }

      const distance = start - now;

      const days = Math.floor(distance / (1000 * 60 * 60 * 24));
      const hours = Math.floor(
        (distance % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)
      );
      const minutes = Math.floor((distance % (1000 * 60 * 60)) / (1000 * 60));
      const seconds = Math.floor((distance % (1000 * 60)) / 1000);

      const parts = [];

      // Handle Days
      if (days > 0) {
        parts.push(`${days}d`);
      }

      // Handle Hours
      if (hours > 0) {
        parts.push(`${hours}h`);
      }

      // Handle Minutes
      if (minutes > 0) {
        parts.push(`${minutes}m`);
      }

      // Handle Seconds (only if days, hours, and minutes are all zero)
      if (days === 0 && hours === 0 && minutes === 0 && seconds > 0) {
        parts.push(`${seconds}s`);
      }

      // Join the parts with spaces
      const time_components = parts.join(" ");

      // Construct the final countdown message using translation
      this.countdown = translate("countdown.start_text", {
        time_components,
      });
    },

    playVideo() {
      this.hovering = true;
      const video = this.$refs.video;
      if (video) {
        // Check if metadata is already loaded
        if (video.readyState >= 1) {
          // HAVE_METADATA
          video.currentTime = Math.min(5, video.duration);
          video.play();
        } else {
          // If metadata isn't loaded, wait for it to load before setting currentTime
          const onLoadedMetadata = () => {
            video.currentTime = Math.min(5, video.duration);
            video.play();
            video.removeEventListener("loadedmetadata", onLoadedMetadata);
          };
          video.addEventListener("loadedmetadata", onLoadedMetadata);
        }
      }
    },
    stopVideo() {
      this.hovering = false;
      const video = this.$refs.video;
      if (video) {
        video.pause();
        video.currentTime = 0; // Reset video to the start
      }
    },
  },
  mounted() {
    this.updateCountdown();
    this.countdownInterval = setInterval(this.updateCountdown, 1000);
  },
  beforeUnmount() {
    clearInterval(this.countdownInterval);
  },
};
</script>
<style scoped>
.formatted-date {
  display: inline-block;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 95%;
  /* Makes sure it respects the container's width */
}

.aspect-video {
  aspect-ratio: 16 / 9;
  /* Set the aspect ratio to 16:9 */
}

.object-cover {
  object-fit: cover;
  /* Make sure the image/video covers the area without distortion */
}

/* Flash Animation for 'live' status icon */
@keyframes flash {
  0% {
    opacity: 1;
  }

  50% {
    opacity: 0.4;
  }

  100% {
    opacity: 1;
  }
}

.flash-animation {
  animation: flash 2s infinite;
}

/*@keyframes pulseRed {

  0%,
  100% {
    background-color: #ef4444;
  }

  50% {
    background-color: #dc2626;
  }
}

.bg-pulse {
  animation: pulseRed 2s infinite ease-in-out;
}*/

@media (min-width: 1536px) and (max-width: 1751px) {
  .stars-icon {
    font-size: 0.65rem;
  }

  .countdown-text {
    font-size: 13px;
    line-height: 18px;
  }
}

@media (min-width: 1280px) and (max-width: 1400px) {
  .stars-icon {
    font-size: 0.65rem;
  }

  .countdown-text {
    font-size: 13px;
    line-height: 18px;
  }
}

@media (min-width: 1024px) and (max-width: 1091px) {
  .stars-icon {
    font-size: 0.75rem;
  }

  .countdown-text {
    font-size: 13px;
    line-height: 18px;
  }
}
</style>